
import React, { useState } from 'react';
import { Menu, X, Settings, Edit, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RenameSessionDialog } from '../dialogs/RenameSessionDialog';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { TopbarLanguageDropdown } from '@/components/common/TopbarLanguageDropdown';
import { useIsMobile } from '@/hooks/use-mobile';

export const AiTopbar: React.FC = () => {
  const { toggleSidebar, isCollapsed } = useSidebar();
  const { currentSession, updateSessionTitle } = useAiAssistant();
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isInlineRenaming, setIsInlineRenaming] = useState(false);
  const [inlineRenameValue, setInlineRenameValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();

  const handleInlineRenameSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!currentSession || !inlineRenameValue.trim() || inlineRenameValue === currentSession.name_session) {
      setIsInlineRenaming(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await updateSessionTitle(currentSession.id, inlineRenameValue.trim());
      if (success) {
        toast.success('Session renamed successfully');
      } else {
        toast.error('Failed to rename session');
        setInlineRenameValue(currentSession.name_session);
      }
    } catch (error) {
      console.error('Error renaming session:', error);
      toast.error('Failed to rename session');
      setInlineRenameValue(currentSession.name_session);
    } finally {
      setIsSubmitting(false);
      setIsInlineRenaming(false);
    }
  };

  const startInlineRenaming = () => {
    if (!currentSession) return;
    setInlineRenameValue(currentSession.name_session);
    setIsInlineRenaming(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsInlineRenaming(false);
    }
  };

  return (
    <div 
      className={cn(
        // Enhanced fixed positioning with iOS-specific classes
        "topbar-fixed-positioning ios-fixed-topbar",
        // Sidebar-aware margin with responsive behavior
        "sidebar-aware-margin",
        !isMobile && !isCollapsed && "sidebar-expanded",
        !isMobile && isCollapsed && "sidebar-collapsed",
        // Enhanced solid background
        "bg-background border-b border-border/30",
        // Layout and sizing
        "flex items-center justify-between px-4",
        isMobile ? "h-12" : "h-14"
      )}
      style={{
        // Enhanced safe area handling
        minHeight: isMobile 
          ? 'calc(3rem + var(--safe-area-inset-top))' 
          : 'calc(3.5rem + var(--safe-area-inset-top))',
        paddingTop: 'var(--safe-area-inset-top)',
        // Enhanced backdrop effects
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        // Force highest z-index to stay above sidebar
        zIndex: 100,
        // iOS-specific positioning lock
        WebkitTransform: 'translateZ(0)',
        transform: 'translateZ(0)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden'
      }}
    >
      <div className="flex items-center">
        <Button 
          variant="ghost" 
          size={isMobile ? "sm" : "icon"}
          onClick={toggleSidebar}
          className={cn(
            "hover:bg-white/10",
            isMobile ? "h-8 w-8 mr-1" : "mr-2"
          )}
        >
          <Menu className={isMobile ? "h-4 w-4" : "h-5 w-5"} />
        </Button>
        
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={isMobile ? "max-w-[180px]" : ""}
        >
          {currentSession ? (
            <div className="flex flex-col">
              {isInlineRenaming ? (
                <form onSubmit={handleInlineRenameSubmit} className="flex items-center gap-2">
                  <Input
                    value={inlineRenameValue}
                    onChange={(e) => setInlineRenameValue(e.target.value)}
                    onBlur={handleInlineRenameSubmit}
                    onKeyDown={handleKeyDown}
                    className={cn(
                      "bg-background/30 border-white/10",
                      isMobile ? "h-6 text-sm" : "h-7 text-lg",
                      "font-semibold"
                    )}
                    autoFocus
                    disabled={isSubmitting}
                  />
                  <Button 
                    type="submit" 
                    variant="ghost" 
                    size="sm"
                    className={cn(
                      isMobile ? "h-6 px-1 text-xs" : "h-7 px-2"
                    )}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '...' : 'Save'}
                  </Button>
                </form>
              ) : (
                <div className="group flex items-center gap-2">
                  <h2 className={cn(
                    "font-semibold truncate",
                    isMobile ? "text-base" : "text-lg"
                  )}>
                    {currentSession.name_session}
                  </h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "opacity-0 group-hover:opacity-100 transition-opacity",
                      isMobile ? "h-4 w-4 p-0" : "h-5 w-5 p-0"
                    )}
                    onClick={startInlineRenaming}
                    title="Rename session"
                  >
                    <Pencil className={isMobile ? "h-2.5 w-2.5" : "h-3.5 w-3.5"} />
                  </Button>
                </div>
              )}
              <p className={cn(
                "text-muted-foreground",
                isMobile ? "text-[10px]" : "text-xs"
              )}>
                {new Date(currentSession.created_at).toLocaleDateString()}
              </p>
            </div>
          ) : (
            <div className="flex flex-col">
              <h2 className={cn(
                "font-semibold",
                isMobile ? "text-base" : "text-lg"
              )}>AI Assistant</h2>
              <p className={cn(
                "text-muted-foreground",
                isMobile ? "text-[10px]" : "text-xs"
              )}>
                Start a new conversation
              </p>
            </div>
          )}
        </motion.div>
      </div>
      
      <div className="flex items-center gap-2">
        {!isMobile && <TopbarLanguageDropdown />}
        
        {currentSession && (
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost" 
                size={isMobile ? "sm" : "icon"}
                className={cn(
                  "hover:bg-white/10",
                  isMobile && "h-8 w-8 px-0"
                )}
              >
                <Edit className={isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} />
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              className={cn(
                "bg-background/95 backdrop-blur-xl border-white/10",
                isMobile ? "w-48 p-2" : "w-60"
              )}
              align="end"
            >
              <div className="space-y-2">
                <h3 className={cn(
                  "font-medium",
                  isMobile ? "text-xs" : "text-sm"
                )}>Session options</h3>
                <Separator className="bg-white/10" />
                <div className="space-y-4 mt-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start bg-background/30 border-white/10"
                    size={isMobile ? "sm" : "default"}
                    onClick={() => setIsRenameDialogOpen(true)}
                  >
                    <Edit className={isMobile ? "h-3 w-3 mr-1" : "h-4 w-4 mr-2"} />
                    <span className={isMobile ? "text-xs" : ""}>Rename Session</span>
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
        
        <Link to="/">
          <Button 
            variant="ghost" 
            size={isMobile ? "sm" : "icon"}
            className={cn(
              "hover:bg-white/10",
              isMobile && "h-8 w-8 px-0"
            )}
          >
            <X className={isMobile ? "h-4 w-4" : "h-5 w-5"} />
          </Button>
        </Link>
      </div>

      {/* Rename Dialog */}
      {currentSession && (
        <RenameSessionDialog
          sessionId={currentSession.id}
          currentName={currentSession.name_session}
          isOpen={isRenameDialogOpen}
          onOpenChange={setIsRenameDialogOpen}
        />
      )}
    </div>
  );
};
